import moment from 'moment';
import { d2f } from '../../utils/common';

export const safeTotal = (chartData: { total: number; gross: number } | undefined, amountType: 'gross' | 'net'): number => {
    const source = amountType === 'gross' ? chartData?.gross : chartData?.total;
    const numeric = Number(source ?? 0);
    return Number.isFinite(numeric) ? numeric : 0;
};

export const safeNumber = (value: unknown): number => {
    const numeric = Number(value ?? 0);
    return Number.isFinite(numeric) ? numeric : 0;
};

export const formatCompactUsd = (value: number): string => {
    const numeric = Number(value ?? 0);
    if (!Number.isFinite(numeric)) {
        return '$0';
    }
    if (Math.abs(numeric) < 1000) {
        return `$${Math.round(numeric).toLocaleString()}`;
    }

    return new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1,
    })
        .format(numeric)
        .replace(/^/, '$');
};

export const formatCompactNumber = (value: number): string => {
    const numeric = Number(value ?? 0);
    if (!Number.isFinite(numeric)) {
        return '0';
    }
    if (Math.abs(numeric) < 1000) {
        return Math.round(numeric).toLocaleString();
    }
    return new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1,
    }).format(numeric);
};

export const countryCodeToFlagEmoji = (countryCode: string | undefined): string => {
    if (!countryCode) {
        return '🏁';
    }
    const normalized = countryCode.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(normalized)) {
        return '';
    }
    const base = 127397;
    return String.fromCodePoint(...normalized.split('').map((char) => base + char.charCodeAt(0)));
};

export const calculateDeltaPercent = (current: number, previous: number): number | null => {
    if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) {
        return null;
    }
    return ((current - previous) / previous) * 100;
};

export const getRangeStartDate = (preset: '7d' | '30d' | '90d' | '1y' | 'all' | 'custom'): Date | undefined => {
    if (preset === 'all' || preset === 'custom') {
        return undefined;
    }
    if (preset === '7d') {
        return moment.utc().subtract(7, 'days').startOf('day').toDate();
    }
    if (preset === '30d') {
        return moment.utc().subtract(30, 'days').startOf('day').toDate();
    }
    if (preset === '90d') {
        return moment.utc().subtract(90, 'days').startOf('day').toDate();
    }
    return moment.utc().subtract(1, 'year').startOf('day').toDate();
};

export const formatRangeLabel = (start: Date | null, end: Date | null): string => {
    if (!start || !end) {
        return 'Select date range';
    }
    return `${moment.utc(start).format('MMM D, YYYY')} – ${moment.utc(end).format('MMM D, YYYY')}`;
};

export const sortSubscribers = (
    subscribers: any[] | undefined,
    metricsById: Record<string, any>,
    defaultMetrics: any,
    filter: string,
    sortBy: string,
    sortDirection: 'asc' | 'desc',
) => {
    if (!subscribers || !Array.isArray(subscribers)) {
        return [];
    }

    const copy = [...subscribers];
    copy.sort((a: any, b: any) => {
        if (sortBy === 'username') {
            return (a.username || '').localeCompare(b.username || '');
        }
        if (sortBy === 'email') {
            return (a.email || '').localeCompare(b.email || '');
        }
        if (sortBy === 'credit') {
            return (
                (a.financial?.sharedCredit ? d2f(a.financial.sharedCredit) : 0) -
                (b.financial?.sharedCredit ? d2f(b.financial.sharedCredit) : 0)
            );
        }
        if (sortBy === 'category') {
            return (a.cognito?.category || '').localeCompare(b.cognito?.category || '');
        }
        if (sortBy === 'isLoggedIn') {
            return Number(Boolean(a.isLoggedIn)) - Number(Boolean(b.isLoggedIn));
        }

        const aMetrics = metricsById[String(a._id)] || defaultMetrics;
        const bMetrics = metricsById[String(b._id)] || defaultMetrics;
        const valueBySortKey: Record<string, number> = {
            earnings: aMetrics.earnings - bMetrics.earnings,
            chargebacks: aMetrics.chargebacks - bMetrics.chargebacks,
            referralEarnings: aMetrics.referralEarnings - bMetrics.referralEarnings,
            ltv: aMetrics.ltv - bMetrics.ltv,
            tips: aMetrics.tips - bMetrics.tips,
            messages: aMetrics.messages - bMetrics.messages,
            posts: aMetrics.posts - bMetrics.posts,
            subscriptions: aMetrics.subscriptions - bMetrics.subscriptions,
            streamTips: aMetrics.streamTips - bMetrics.streamTips,
            newSubscribersToday: aMetrics.newSubscribersToday - bMetrics.newSubscribersToday,
            activeFans: aMetrics.activeFans - bMetrics.activeFans,
            activeFollowing: aMetrics.activeFollowing - bMetrics.activeFollowing,
            performerTop: aMetrics.performerTop - bMetrics.performerTop,
            payoutAvailable: aMetrics.payoutAvailable - bMetrics.payoutAvailable,
            payoutPending: aMetrics.payoutPending - bMetrics.payoutPending,
            referralsBalance: aMetrics.referralsBalance - bMetrics.referralsBalance,
            todayStories: aMetrics.todayStories - bMetrics.todayStories,
            todayPosts: aMetrics.todayPosts - bMetrics.todayPosts,
            todayChats: aMetrics.todayChats - bMetrics.todayChats,
            todayStreams: aMetrics.todayStreams - bMetrics.todayStreams,
            profileVisitorsTotal: aMetrics.profileVisitorsTotal - bMetrics.profileVisitorsTotal,
            profileVisitorsGuests: aMetrics.profileVisitorsGuests - bMetrics.profileVisitorsGuests,
            profileVisitorsUsers: aMetrics.profileVisitorsUsers - bMetrics.profileVisitorsUsers,
            profileVisitorsSubscribers: aMetrics.profileVisitorsSubscribers - bMetrics.profileVisitorsSubscribers,
        };

        return valueBySortKey[sortBy] || 0;
    });

    const filtered = copy.filter((subscriber) => (subscriber.username || '').toLowerCase().includes(filter.toLowerCase()));
    if (sortDirection === 'desc') {
        filtered.reverse();
    }

    return filtered;
};
